import { useEffect, useState } from "react";

/**
 * Tracks connectivity so the app can show the offline page itself.
 *
 * With server rendering, a failed navigation was answered by the service worker
 * with the pre-rendered `/offline` HTML. A single-page app has only one shell
 * for every route, so the worker can no longer choose what the user sees — the
 * shell boots at whatever URL was requested and the decision has to be made
 * here instead.
 *
 * `navigator.onLine` only reports whether the machine has a network interface,
 * not whether the portal is reachable, so it is trusted in one direction only:
 * `false` is reliable, `true` is not. The pages therefore keep their own error
 * handling for a request that fails while the browser believes it is online.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}
